import { type LearningAssessmentStoreState, state as assessmentState } from "@/store/learning/assessment/state";
import { LearningMenuType } from "@/components/Learning/LearningMiddle/config/LearningMenuType";
import { LearningCourseModel } from "~~/package/mapper/learning/model/LearningCourseModel";
import { TaskModel } from "~~/package/mapper/learning/model/TaskModel";
import type TaskCompleteResponse from "~~/package/apis/learning/response/TaskCompleteResponse";
import type TaskAnswerResponse from "~~/package/apis/learning/response/TaskAnswerResponse";
import { LinuxInstructionModel } from "~~/package/mapper/learning/model/linux/LinuxInstructionModel";
import type { Unsubscribe, WebContainer, WebContainerProcess } from "@webcontainer/api";
import type FileData from "~~/package/types/FileData";
import type { LearningFileSystemTree } from "~~/package/types/LearningFileSystemTree";
import { Readable } from "stream";
import { LearningTokenData } from "~~/package/mapper/learning/model/LearningTokenData";
import type { CodeCustomFunctionModel } from "~~/package/mapper/learning/model/CodeCustomFunctionModel";
import {
    createDirectoryState,
    createFrontLearningState,
    createLinuxLearningState
} from "@/store/learning/state/factories";

interface AwsCredentials {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
}

export interface LearningStoreState extends LearningAssessmentStoreState {
    oldLangType: string;
    isDarkMode: boolean;

    isInitData: boolean;
    editorKey: number;
    currentCode: string;
    codeOutPut: string[];
    errorOutPut: string[];
    validSet: string;
    score: number | undefined;
    descriptiveAnswer: string;
    linuxDescriptiveAnswer: string;
    imageList: string[];
    isShowImage: boolean;
    isRunning: boolean;
    isUpdateCode: boolean;
    isErrorResult: boolean;
    isSuccessResult: boolean;
    isLastTask: boolean;
    isNeverRun: boolean;
    isFrontRun: boolean;
    isChangedButNotSubmit: boolean;
    isShowingDescriptiveAnswer: boolean;
    isSubmitButtonClicked: boolean;
    isSaved: boolean | null;
    isEmbedContentChanged: boolean;
    unsubmittedChangeByTaskId: Record<number, boolean>;

    learningMessage: boolean | null;
    correctCode: string;

    isHideBarsToggle: boolean;

    menuType: LearningMenuType;
    menuActiveList: LearningMenuType[];
    isShowActivityMenu: boolean;
    isChangeActivityMenuHeight: boolean;
    isOrderMenuOpen: boolean;
    isSelectedSelectionId: number;
    draggingActivityWidget: {
        isOrderWidget: boolean,
        isSelectedCard: boolean,
        id: number
    }
    mousePosition: {x: number, y: number}
    contentPosition: { height: number, top: number }

    splitPane: {
        leftSize: number;
        rightSize: number;
    }

    taskComplete: TaskCompleteResponse | null;
    taskAnswer: TaskAnswerResponse | null;

    course: LearningCourseModel;
    task: TaskModel;

    tokenData: LearningTokenData;

    isShowOutputDescription: boolean;
    outputDescription: string;
    errorGuide: string;

    // Directory 기능
    directory: {
        credentials?: AwsCredentials | null;
        credentialProvider?: any;
        isInitCredentials: boolean;
        isInitDirectory: boolean;
        tree: LearningFileSystemTree;
        rollbackTree: LearningFileSystemTree;
        selectedFiles: FileData[];
        rollbackPathList: string[];
        isUploaded: boolean | null;
        isHideFile: boolean[];
        modifiedFiles: FileData[];
        validSetTree: LearningFileSystemTree;
        analysisSetTree: LearningFileSystemTree;
    }

    // Front 실습
    frontLearning: {
        webContainer: WebContainer | null;
        iframeEnable: boolean;
        defaultLocation: string;
        currentLocation: string;
        webContainerProcessList: WebContainerProcess[];
        webContainerTestProcessList: WebContainerProcess[];
        unsubscribeList: Unsubscribe[];
        testResult: string;
        nodeModules: {[key:string]: Buffer|Uint8Array|Blob|string|Readable};
        terminalStreamData: string[];
        isShowToastMessage: boolean;
    }

    // Linux 실습
    linuxLearning: {
        instructionList: LinuxInstructionModel[],
        isInstructionAllDone: boolean
    }

    screenshotDimmed: boolean;

    // task validation
    isValidate: boolean;

    // stop code
    socket: WebSocket | null;

    // editor functions
    customFunctions: CodeCustomFunctionModel[];
}

export const state = (): LearningStoreState => ({
    ...assessmentState(),
    oldLangType: "EN",
    isDarkMode: true,

    editorKey: 0,
    currentCode: "",
    isInitData: false,
    codeOutPut: [],
    errorOutPut: [],
    validSet: "",
    score: undefined,
    descriptiveAnswer: "",
    linuxDescriptiveAnswer: "",
    imageList: [],
    isShowImage: false,
    isRunning: false,
    isUpdateCode: true,
    isErrorResult: false,
    isSuccessResult: false,
    isLastTask: false,
    isNeverRun: true,
    isFrontRun: false,
    isChangedButNotSubmit: false,
    isShowingDescriptiveAnswer: false,
    isSubmitButtonClicked: false,
    isSaved: null,
    isEmbedContentChanged: false,
    unsubmittedChangeByTaskId: {},

    learningMessage: null,
    correctCode: "",

    isHideBarsToggle: false,

    menuType: LearningMenuType.None,
    menuActiveList: [],
    isShowActivityMenu: false,
    isChangeActivityMenuHeight: false,
    isOrderMenuOpen: false,
    isSelectedSelectionId: -1,
    draggingActivityWidget: {
        isOrderWidget: false,
        isSelectedCard: false,
        id: -1,
    },
    mousePosition: {x: -1, y: -1},
    contentPosition: { height: 0, top: 0 },
    splitPane: { leftSize: 60, rightSize: 40 },
    taskComplete: null,
    taskAnswer: null,

    course: new LearningCourseModel(),
    task: new TaskModel(),

    tokenData: new LearningTokenData(),

    isShowOutputDescription: false,
    outputDescription: "",
    errorGuide: "",

    directory: createDirectoryState(),

    frontLearning: createFrontLearningState(),

    linuxLearning: createLinuxLearningState(),

    screenshotDimmed: false,

    isValidate: false,

    socket: null,

    customFunctions: [],
});
